/* eslint-disable max-lines-per-function */
import React from "react";
import { useMutation } from "@apollo/client";
import { Button, ButtonProps, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, ButtonGroup } from "@mui/material";
import { Link } from "react-router-dom";
import { CopyButton } from "components/haven-design/copy-button";
import { useSnackbar } from "components/haven-design/snackbar";
import {
  AdminDetailedListUsersQuery_adminApplicants_interests as Interest,
  AdminUpdateInterest,
  AdminUpdateInterestVariables,
} from "types/gql-types";
import logger from "services/logger";
import { UPDATE_INTEREST } from "../../interests/queries";

type InterestListProps = {
  interests: Interest[] | null
}

const InterestsList: React.FC<InterestListProps> = ({ interests }) => {
  const { openSnackbar } = useSnackbar();

  const [updateInterest] = useMutation<
  AdminUpdateInterest,
  AdminUpdateInterestVariables
  >(UPDATE_INTEREST, {
    refetchQueries: ["AdminListUsersQuery"],
  });

  const promptTransfer = (interestId: string): ButtonProps["onClick"] => async (event) => {
    event.preventDefault();

    // eslint-disable-next-line no-alert
    const userId = prompt("Enter the User ID to transfer this application to")?.trim();
    if (!userId) return;

    try {
      await updateInterest({ variables: { id: interestId, userId } });

      openSnackbar({ message: `Transferred Interest to user ${userId} Successfully`, status: "success" });
    } catch (e) {
      openSnackbar({ message: "Transferring Interest Failed", status: "error" });
      logger.sendError(e as Error);
    }
  };

  const promptCorrectSubmissionDate = (interestId: string): ButtonProps["onClick"] => async (event) => {
    event.preventDefault();

    // eslint-disable-next-line no-alert
    const submittedAt = prompt("Enter the new submission date for this application, in the format YYYY-MM-DD")?.trim();
    if (!submittedAt) return;

    try {
      await updateInterest({ variables: { id: interestId, submittedAt } });

      openSnackbar({ message: `Updated Interest's submission date to ${submittedAt} Successfully`, status: "success" });
    } catch (e) {
      logger.sendError(e as Error);
      openSnackbar({ message: "Updating Interest's submission date Failed", status: "error" });
    }
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Property / List</TableCell>
            <TableCell>Application Contact</TableCell>
            <TableCell>Status / Applied At</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {!interests?.length && (
            <TableRow>
              <TableCell align="center" colSpan={7}>
                <strong>Applicant has no interests</strong>
              </TableCell>
            </TableRow>
          )}

          {interests?.map((row) => (
            <TableRow key={row.id}>
              <TableCell>
                <strong>{ row.id.substring(30) }</strong>
                <CopyButton text={row.id} />
              </TableCell>

              <TableCell>
                <Link
                  to={`/dash/manager/lists/${row.interestList?.id}/p/1/interests/${row.id}`}
                >
                  <strong>{row.interestList?.organization?.name}</strong>
                  <br />
                  {row.interestList?.property?.name}
                  <br />
                  <small>(List: {row.interestList?.name})</small>
                </Link>
              </TableCell>

              <TableCell>
                <strong>{row.householdMember0FirstName} {row.householdMember0LastName}</strong>
                <div>{row.householdMember0Email}</div>
                {row.householdMember0PrimaryPhone && (
                  <div>
                    P {row.householdMember0PrimaryPhone}
                    <small>
                      {row.householdMember0PrimaryPhoneType &&
                      ` (${row.householdMember0PrimaryPhoneType})`}
                    </small>
                  </div>
                )}
                {row.householdMember0SecondaryPhone && (
                  <div>
                    S {row.householdMember0SecondaryPhone}
                    <small>
                      {row.householdMember0SecondaryPhoneType &&
                      ` (${row.householdMember0SecondaryPhoneType})`}
                    </small>
                  </div>
                )}
              </TableCell>

              <TableCell>
                <div>{row.status.name}</div>
                {row.submittedAt ? (
                  <time dateTime={row.submittedAt.toString()}>
                    {new Date(row.submittedAt * 1000).toLocaleString()}
                  </time>
                ) : (
                  <p>No Submission Date Available</p>
                )}
              </TableCell>
              <TableCell>
                <ButtonGroup color="primary" size="small" variant="outlined" aria-label="interest actions">
                  <Button onClick={promptTransfer(row.id)}>
                    Transfer
                  </Button>
                  <Button onClick={promptCorrectSubmissionDate(row.id)}>
                    Correct Date
                  </Button>
                </ButtonGroup>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default InterestsList;
